/**
 * HIPAA Letter Lint — Client-side diagnosis scanner
 * --------------------------------------------------
 * Used by the green "HIPAA OK" / amber "Review" chip on FMLA, ADA, and other
 * HIPAA-restrictive admin letters. Provides a fast visual confirmation that
 * the LLM did not leak a diagnosis name into the letter body.
 *
 * This is a BEST-EFFORT lint — not a substitute for provider review.
 * It scans the rendered letter text for a curated lexicon of common
 * diagnosis terms and ICD-10-style code patterns. False positives are
 * minimized by:
 *   - Word-boundary matching (so "depressed mood" doesn't trip "depression")
 *   - Allowlist of generic phrases that legitimately appear ("serious health
 *     condition", "qualifying medical condition") and are HIPAA-safe.
 *   - Whitelisting symptom/anatomy words even if they look clinical.
 */

// Letter types where HIPAA hard rules apply (must match the backend set
// in /app/backend/server.py HIPAA_RESTRICTIVE_LETTER_TYPES).
export const HIPAA_RESTRICTIVE_LETTER_TYPES = new Set([
  'fmla_intermittent',
  'fmla_continuous',
  'ada_accommodations',
  'school_504_accommodation',
  'housing_accommodation',
  'parking_permit',
  'std_claim_support',
  'std_extension',
  'jury_duty_excuse',
]);

// Curated diagnosis-name lexicon. Each entry is matched as a whole-word
// case-insensitive substring. Keep this list focused on the conditions the
// LLM has historically leaked. Symptom words ("pain", "fatigue") are
// intentionally NOT included — those are HIPAA-safe.
const DIAGNOSIS_LEXICON = [
  // Mental / behavioral
  'PTSD', 'post-traumatic stress disorder', 'post traumatic stress',
  'depression', 'major depressive disorder', 'MDD',
  'anxiety disorder', 'generalized anxiety disorder', 'GAD',
  'bipolar disorder', 'bipolar',
  'schizophrenia', 'schizoaffective',
  'ADHD', 'attention deficit', 'autism', 'autism spectrum',
  'OCD', 'obsessive-compulsive disorder', 'obsessive compulsive disorder',
  'panic disorder',
  // Neurology / chronic
  'migraine', 'migraines',
  'epilepsy', 'seizure disorder',
  'multiple sclerosis', 'MS',
  'parkinson', "parkinson's", 'parkinsons',
  'alzheimer', "alzheimer's",
  'TBI', 'traumatic brain injury', 'concussion syndrome',
  'narcolepsy', 'fibromyalgia',
  // Cardio / metabolic
  'diabetes', 'type 1 diabetes', 'type 2 diabetes', 'T1DM', 'T2DM',
  'hypertension', 'hypertensive',
  'congestive heart failure', 'CHF', 'coronary artery disease', 'CAD',
  'atrial fibrillation', 'AFib',
  // Respiratory
  'asthma', 'COPD', 'chronic obstructive pulmonary disease',
  'sleep apnea', 'obstructive sleep apnea', 'OSA',
  // GI
  "Crohn's disease", 'Crohn disease', "Crohn's",
  'ulcerative colitis', 'IBS', 'irritable bowel syndrome',
  'celiac disease', 'GERD',
  // Endocrine / GYN / Onc
  'endometriosis', 'PCOS', 'polycystic ovary syndrome',
  'hypothyroidism', 'hyperthroidism', "hashimoto's", 'graves disease',
  'cancer', 'carcinoma', 'leukemia', 'lymphoma', 'melanoma',
  // Autoimmune
  'lupus', 'SLE', 'rheumatoid arthritis', 'psoriasis', 'eczema',
  // Musculoskeletal — only diagnostic terms, not symptoms
  'osteoarthritis', 'degenerative disc disease', 'sciatica',
  'ACL tear', 'meniscus tear',
  // Other
  'HIV', 'AIDS', 'hepatitis', 'tuberculosis', 'TB',
  'pregnancy loss', 'miscarriage',
];

// Phrases that LOOK clinical but are explicitly HIPAA-safe in our letters.
// We strip these before scanning so they never produce false positives.
const ALLOWLIST_PHRASES = [
  'serious health condition',
  'qualifying medical condition',
  'chronic medical condition',
  'episodic medical condition',
  'qualifying condition under the ADA',
  'qualifying condition under the FMLA',
  'medical condition',
];

// ICD-10 codes look like A12.34 or A123.45 — never appropriate in a HIPAA
// restrictive letter.
const ICD10_PATTERN = /\b[A-TV-Z][0-9][0-9AB](?:\.[0-9A-Z]{1,4})?\b/g;

/**
 * Scan a HIPAA-restrictive letter for diagnosis leakage.
 *
 * @param {string} text The rendered letter body
 * @returns {{ ok: boolean, hits: string[], icd10Hits: string[] }}
 *          ok: true iff no diagnosis terms or ICD-10 codes were found
 *          hits: unique diagnosis terms detected (preserving original casing)
 *          icd10Hits: ICD-10 code strings detected
 */
export const scanForDiagnoses = (text) => {
  if (!text || typeof text !== 'string') {
    return { ok: true, hits: [], icd10Hits: [] };
  }

  // Strip allowlisted phrases first so they never produce false positives.
  let scrubbed = text;
  for (const phrase of ALLOWLIST_PHRASES) {
    const re = new RegExp(phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    scrubbed = scrubbed.replace(re, ' ');
  }

  const hits = new Set();
  for (const term of DIAGNOSIS_LEXICON) {
    // Word-boundary match. Escape regex metacharacters inside the term.
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(^|[^a-z0-9])(${escaped})([^a-z0-9]|$)`, 'i');
    const m = scrubbed.match(re);
    if (m) hits.add(m[2]);
  }

  const icd10Hits = Array.from(new Set(
    (scrubbed.match(ICD10_PATTERN) || []).map((c) => c.toUpperCase())
  ));

  return {
    ok: hits.size === 0 && icd10Hits.length === 0,
    hits: Array.from(hits),
    icd10Hits,
  };
};

/**
 * Does this letter_type need HIPAA scanning?
 */
export const isHipaaRestrictive = (letterType) =>
  HIPAA_RESTRICTIVE_LETTER_TYPES.has(letterType);
